import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  FeatureFlagMode,
  FeatureFlagState,
  FeatureFlagsApi,
} from '@internal/plugin-feature-flags-common';
import { featureFlagsApiRef } from '@internal/plugin-feature-flags-common/api';

export type { FeatureFlagState, FeatureFlagsApi, FeatureFlagMode };
export { featureFlagsApiRef };

/**
 * HTTP client for the feature-flags backend plugin.
 */
export class FeatureFlagsClient implements FeatureFlagsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('feature-flags');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message =
        typeof body.error === 'object' &&
        body.error &&
        typeof body.error.message === 'string'
          ? body.error.message
          : typeof body.error === 'string'
            ? body.error
            : `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(message);
    }

    return response.json() as Promise<T>;
  }

  async listFlags(): Promise<FeatureFlagState[]> {
    const result = await this.request<{ flags: FeatureFlagState[] }>('/flags');
    return result.flags;
  }

  async setFlag(key: string, mode: FeatureFlagMode): Promise<FeatureFlagState> {
    return this.request<FeatureFlagState>(
      `/flags/${encodeURIComponent(key)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ mode }),
      },
    );
  }
}
